import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import styled from "styled-components";

import { Button, LinkButton } from "../../../../components/Button";
import * as Fonts from "../../../../components/Fonts";
import Input from "../../../../components/Form/Input/input";
import { FormGroup } from "../../../../components/Form/styles";
import { KeepoLoader } from "../../../../components/KeepoLoader";
import { SubHeader } from "../../../../components/SubHeader";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import { useAppState } from "../../../../contexts/App";
import { api, hasActiveSubscription, SitePassword } from "../../../../services/api";
import { isApiError } from "../../../../services/api/errors";
import { Card, CardFooter, Container, Section, ToggleRow } from "../../styles";

const prefix = "pages.settings.sitePassword";

/** A password at least this long (it's shared with visitors, not a login): the API checks it too */
export const MIN_LENGTH = 4;

// Easy to read out and type: no 0/O, 1/l/I
const ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";

/** 8 random letters and digits (crypto.getRandomValues), easy to share. */
export const generatePassword = (length = 8) => {
  const values = crypto.getRandomValues(new Uint32Array(length));
  return Array.from(values, value => ALPHABET[value % ALPHABET.length]).join("");
};

/**
 * Keepo com senha: the toggle and the password visitors type to see the Keepo (the owner can see and copy it, or
 * generate one). Turning it on or changing the password is PLUS; a Keepo that left PLUS stays protected and can
 * only turn it off here.
 */
export const SitePasswordPage = () => {
  const { t } = useTranslation();
  const { appState, setAppState } = useAppState();
  const siteId = appState.editingSiteId as string;
  // As the API sees it (active subscription), not just the plan: a PLUS that lapsed is blocked there too
  const isPlus = hasActiveSubscription(appState.editingSiteSubscription);

  // undefined: loading; null: couldn't load
  const [saved, setSaved] = useState<SitePassword | null>();
  const [enabled, setEnabled] = useState(false);
  const [password, setPassword] = useState("");
  const [isVisible, setVisible] = useState(false);
  const [isCopied, setCopied] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState<"short" | "plus" | "save">();

  useEffect(() => {
    // A late answer for the Keepo we left must not replace this one's
    let isCurrent = true;
    setSaved(undefined);
    api
      .getSitePassword(siteId)
      .then(value => {
        if (!isCurrent) {
          return;
        }
        setSaved(value);
        setEnabled(value.enabled);
        setPassword(value.password || "");
      })
      .catch(() => isCurrent && setSaved(null));
    return () => {
      isCurrent = false;
    };
  }, [siteId]);

  const hasChanges = useMemo(
    () => !!saved && (enabled !== saved.enabled || password.trim() !== (saved.password || "")),
    [enabled, password, saved]
  );

  const save = async () => {
    setError(undefined);
    const trimmed = password.trim();
    if (enabled && trimmed.length < MIN_LENGTH) {
      setError("short");
      return;
    }
    setSaving(true);
    try {
      const changed = trimmed !== (saved?.password || "");
      const next = await api.updateSitePassword(siteId, enabled, changed && trimmed ? trimmed : undefined);
      // The settings list says "Ativo" from this
      setAppState({ ...appState, editingSitePasswordProtected: next.enabled });
      setSaved(next);
      setEnabled(next.enabled);
      setPassword(next.password || "");
    } catch (e) {
      setError(isApiError(e, "INVALID_PASSWORD") ? "short" : isApiError(e, "PLUS_REQUIRED") ? "plus" : "save");
    } finally {
      setSaving(false);
    }
  };

  // Only the saved password: what's in the field may not be what visitors can type yet
  const canCopy = !!saved?.password && password.trim() === saved.password;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(saved?.password || "");
    } catch {
      // No clipboard (e.g. plain http): nothing was copied, so no "Copied!"
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const discard = () => {
    setError(undefined);
    setEnabled(!!saved?.enabled);
    setPassword(saved?.password || "");
  };

  const canSave = hasChanges && !isSaving;

  // Never had it and can't turn it on (not PLUS): nothing to do here (one that's still on opens, to turn it off)
  if (saved && !isPlus && !saved.enabled) {
    return <Navigate to="/settings" replace />;
  }

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.title`)}
        crumbs={[{ label: t("pages.settings.header.title"), to: "/settings" }]}
        hasUnsavedChanges={hasChanges}
      />

      <Section>
        <Fonts.Label>{t(`${prefix}.section`)}</Fonts.Label>
        {saved === undefined ? (
          <Card aria-busy="true">
            <Center>
              <KeepoLoader size={24} color="var(--color-neutral-white)" />
            </Center>
          </Card>
        ) : saved === null ? (
          <Card>
            <Message>{t(`${prefix}.loadError`)}</Message>
          </Card>
        ) : (
          <Card
            as="form"
            onSubmit={(event: React.FormEvent) => {
              event.preventDefault();
              if (canSave) {
                save();
              }
            }}>
            <ToggleRow>
              <div className="text">
                <h6>{t(`${prefix}.toggle`)}</h6>
                <p className="description">{t(`${prefix}.toggleHint`)}</p>
              </div>
              {/* FREE: only turning it off */}
              <ToggleSwitch
                value={enabled}
                setValue={setEnabled}
                disabled={isSaving || (!isPlus && !enabled)}
                label={t(`${prefix}.toggle`)}
              />
            </ToggleRow>
            <div className="divider" />

            <FormGroup>
              <Input
                id="site-password"
                type={isVisible ? "text" : "password"}
                label={t(`${prefix}.password`)}
                value={password}
                maxLength={64}
                ignoreLengthInfo
                autoCapitalize="none"
                disabled={!isPlus || isSaving}
                onChange={event => setPassword(event.target.value)}
                hint={t(`${prefix}.passwordHint`)}
              />
            </FormGroup>
            {/* What to do with the password: see it, copy it to share, or make up a new one */}
            <Actions>
              <button type="button" onClick={() => setVisible(!isVisible)}>
                {isVisible ? t(`${prefix}.hide`) : t(`${prefix}.show`)}
              </button>
              <button type="button" disabled={!canCopy} onClick={copy}>
                {isCopied ? t(`${prefix}.copied`) : t(`${prefix}.copy`)}
              </button>
              {isPlus && (
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => {
                    setPassword(generatePassword());
                    setVisible(true);
                    setError(undefined);
                  }}>
                  {t(`${prefix}.generate`)}
                </button>
              )}
            </Actions>

            {!isPlus && <Message>{t(`${prefix}.freeNote`)}</Message>}

            <CardFooter>
              {error && (
                <p className="error" role="alert">
                  {t(`${prefix}.errors.${error}`)}
                </p>
              )}
              <div className="actions">
                {hasChanges && <LinkButton onClick={discard}>{t("pages.settings.unsavedBar.discard")}</LinkButton>}
                <Button
                  type="INFO"
                  size="MD"
                  role="button"
                  disabled={!canSave}
                  aria-disabled={!canSave}
                  onClick={() => canSave && save()}>
                  {isSaving ? t("pages.settings.actions.save.button.titleSaving") : t("actions.save")}
                </Button>
              </div>
            </CardFooter>
          </Card>
        )}
      </Section>
    </Container>
  );
};

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 0;
`;

/* Small pill buttons under the field, left-aligned (the card's own actions are at its footer) */
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: -0.25rem;

  button {
    padding: 0.5rem 0.875rem;
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 100px;
    transition: background-color 0.15s;

    &:hover:not(:disabled) {
      background: var(--color-neutral-light);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
`;

const Message = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-neutral-150);
`;
